/**
 * Session token + best-effort browser fingerprint.
 *
 * The JWT lives in `sessionStorage` so a reload survives but the token is
 * gone the moment the tab closes. Long-lived re-auth is handled by the
 * platform passkey (see `lib/api.ts` and `pages/Login.tsx`).
 */

const TOKEN_STORAGE_KEY = 'vpn.token';

export function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    else sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* sessionStorage may be unavailable in private mode; ignore */
  }
}

/**
 * SHA-256 of a few stable browser bits, hex-encoded. Surfaced in the admin
 * approval message so the operator can spot something obviously off (a new
 * platform, a country mismatch). Not used for auth.
 */
export async function computeFingerprint(): Promise<string> {
  const parts = [
    navigator.userAgent,
    navigator.language,
    navigator.languages?.join(',') ?? '',
    navigator.platform,
    `${screen.width}x${screen.height}`,
    `dpr:${window.devicePixelRatio}`,
    `tz:${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
  ];
  const buf = new TextEncoder().encode(parts.join('|'));
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return bytesToHex(new Uint8Array(hash));
}

function bytesToHex(bytes: Uint8Array): string {
  const out = new Array<string>(bytes.length);
  for (let i = 0; i < bytes.length; i++) out[i] = bytes[i]!.toString(16).padStart(2, '0');
  return out.join('');
}
